import { apiRequest } from '@/shared/api';

export interface CrmContactCardLink {
  id: string;
  display_name: string;
  pipeline: string;
  stage: string;
  is_main?: boolean;
}

export interface CrmContactChannel {
  id: string;
  kind: string;
  value: string;
  is_primary?: boolean;
  chat_external_id?: string;
  dialog_id?: string;
}

/** @deprecated use CrmContactChannel */
export type CrmContactHandle = CrmContactChannel;

export interface CrmContactDetail {
  id: string;
  amocrm_contact_id?: number;
  name: string;
  first_name?: string;
  last_name?: string;
  channel?: string;
  fields: Record<string, string>;
  channels: CrmContactChannel[];
  cards: CrmContactCardLink[];
  amocrm_updated_at?: string;
  updated_at: string;
}

export interface CrmContactListItem {
  id: string;
  amocrm_contact_id?: number;
  name: string;
  phone?: string; // primary phone channel value
  email?: string; // primary email channel value
  channel?: string;
  card_count: number;
  updated_at: string;
}

export interface CrmContactSummary {
  id: string;
  amocrm_contact_id?: number;
  name: string;
  phone?: string;
  email?: string;
  channel?: string;
  is_main?: boolean;
}

export async function listCrmContacts(query = ''): Promise<CrmContactListItem[]> {
  const q = query.trim() ? `?q=${encodeURIComponent(query.trim())}` : '';
  const body = await apiRequest<{ contacts: CrmContactListItem[] }>(`/crm/contacts${q}`);
  return body.contacts ?? [];
}

export async function getCrmContact(contactId: string): Promise<CrmContactDetail> {
  return apiRequest<CrmContactDetail>(`/crm/contacts/${encodeURIComponent(contactId)}`);
}

export async function updateCrmContact(
  contactId: string,
  input: {
    name?: string;
    fields?: Record<string, string>;
  },
): Promise<CrmContactDetail> {
  return apiRequest<CrmContactDetail>(`/crm/contacts/${encodeURIComponent(contactId)}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function addCrmContactChannel(
  contactId: string,
  input: { kind: string; value: string; is_primary?: boolean },
): Promise<CrmContactChannel> {
  return apiRequest<CrmContactChannel>(
    `/crm/contacts/${encodeURIComponent(contactId)}/channels`,
    { method: 'POST', body: input },
  );
}

export async function updateCrmContactChannel(
  contactId: string,
  channelId: string,
  input: { value: string },
): Promise<CrmContactChannel> {
  return apiRequest<CrmContactChannel>(
    `/crm/contacts/${encodeURIComponent(contactId)}/channels/${encodeURIComponent(channelId)}`,
    { method: 'PATCH', body: input },
  );
}

export async function unlinkCrmContactChannel(
  contactId: string,
  channelId: string,
): Promise<void> {
  await apiRequest(
    `/crm/contacts/${encodeURIComponent(contactId)}/channels/${encodeURIComponent(channelId)}`,
    { method: 'DELETE' },
  );
}

export async function addCrmCardContact(
  cardId: string,
  input: { name: string; phone?: string },
): Promise<CrmContactSummary> {
  return apiRequest<CrmContactSummary>(`/crm/cards/${encodeURIComponent(cardId)}/contacts`, {
    method: 'POST',
    body: input,
  });
}

export async function linkCrmCardContact(
  cardId: string,
  contactId: string,
): Promise<CrmContactSummary> {
  return apiRequest<CrmContactSummary>(`/crm/cards/${encodeURIComponent(cardId)}/contacts`, {
    method: 'POST',
    body: { contact_id: contactId },
  });
}

export async function unlinkCrmCardContact(cardId: string, contactId: string): Promise<void> {
  await apiRequest(`/crm/cards/${encodeURIComponent(cardId)}/contacts/${encodeURIComponent(contactId)}`, {
    method: 'DELETE',
  });
}
